/**
 * Règles d'annulation conformes aux CGV
 * Article 2-4 : Annulation < 14 jours ouvrés = 50% du forfait
 * Article 2-5 : Report < 7 jours = 400€ HT
 */

export interface CancellationFees {
  hasFees: boolean;
  message: string;
  percentage?: number;
  fixedAmount?: number;
  daysRemaining: number;
  businessDaysRemaining: number;
}

/**
 * Compte le nombre de jours ouvrés entre deux dates (du lundi au vendredi)
 */
export function calculateBusinessDays(startDate: Date, endDate: Date): number {
  let count = 0;
  const current = new Date(startDate);
  
  while (current <= endDate) {
    const dayOfWeek = current.getDay();
    // 0 = dimanche, 6 = samedi
    if (dayOfWeek !== 0 && dayOfWeek !== 6) {
      count++;
    }
    current.setDate(current.getDate() + 1);
  }
  
  return count;
}

/**
 * Calcule les frais d'annulation selon les CGV
 */
export function calculateCancellationFees(
  bookingDate: Date,
  bookingType: 'shooting' | 'preparation' | 'editorial'
): CancellationFees {
  const now = new Date();
  const timeDiff = bookingDate.getTime() - now.getTime();
  const daysRemaining = Math.ceil(timeDiff / (1000 * 60 * 60 * 24));
  const businessDaysRemaining = calculateBusinessDays(now, bookingDate);

  // Seuls les tournages ont des frais d'annulation (pas les préparations ni les éditoriaux)
  if (bookingType === 'preparation' || bookingType === 'editorial') {
    return {
      hasFees: false,
      message: '',
      daysRemaining,
      businessDaysRemaining,
    };
  }

  // Article 2-4 : Annulation < 14 jours ouvrés = 50% du forfait
  if (businessDaysRemaining < 14) {
    return {
      hasFees: true,
      message: `En annulant à moins de 14 jours ouvrés (actuellement ${businessDaysRemaining} jours ouvrés), des frais d'annulation de 50% du forfait seront appliqués conformément à l'article 2-4 de nos CGV.`,
      percentage: 50,
      daysRemaining,
      businessDaysRemaining,
    };
  }

  return {
    hasFees: false,
    message: '✓ Aucun frais d\'annulation ne sera appliqué (plus de 14 jours ouvrés avant le tournage).',
    daysRemaining,
    businessDaysRemaining,
  };
}

/**
 * Message de confirmation d'annulation avec les frais applicables
 */
export function getCancellationConfirmationMessage(fees: CancellationFees): {
  title: string;
  description: string;
} {
  if (!fees.hasFees) {
    return {
      title: 'Annuler ce tournage ?',
      description: 'Êtes-vous sûr de vouloir annuler ce tournage ? Cette action est irréversible. Aucun frais d\'annulation ne sera appliqué.',
    };
  }

  return {
    title: '⚠️ Frais d\'annulation applicables',
    description: `${fees.message}\n\nÊtes-vous sûr de vouloir continuer ?`,
  };
}
